import React, { useEffect, useState } from "react";
import SaveButton from "../../../components/Buttons/SaveButton";
import { FormConfig } from "../../../components/Fields/Types";
import useValidation from "../../../components/Hooks/ValidationHook/useValidation";
import Popup from "../../../components/Popups/Popup";
import { useNotifications } from "../../../contexts/notificationContext";
import WorkflowTaskTypeEnum from "../../../enums/WorkflowTaskTypeEnum";
import AttachmentTypeModel from "../../../models/AttachmentTypeModel";
import CodeTaskParameterInfo from "../../../models/CodeTaskParameterInfo";
import WorkflowTaskAddDocumentModel from "../../../models/WorkflowTaskAddDocumentModel";
import DataService from "../../../services/data.service";
import { PopupProps } from "../../../types/PopupProps";
import { DataResult } from "../../../types/Result";
import WorkflowTaskControl from "../Controls/WorkflowTaskControl";

interface Props extends PopupProps {
    workflowDefinitionId: number
    workflowStepDefinitionId: number
}

export interface WorkflowTaskDefinitionCreateModel {
    workflowStepDefinitionId: number
    taskTypeId: WorkflowTaskTypeEnum
    name: string
    taskOrder: number
    isRequired: boolean
    forwardsWorkflow: boolean
    duration: number
    reminderInterval?: number
    assignToFunctionalRoleId?: number
    escalateToFunctionalRoleId?: number
    initialTaskStatusId?: number
    description: string
    codeTaskName: string
    parameters: string

    businessPartnerTypeId?: number
    attachmentTypeId?: number
    attachmentRequired?: boolean
    title?: string
    attachmentCount?: number
    userDefinedFieldsIds: number[]
    codeTaskParameterInfos: CodeTaskParameterInfo[]
    documents: WorkflowTaskAddDocumentModel[]
}

const AddWorkflowTask: React.FC<Props> = ({ show, onHide, workflowDefinitionId, workflowStepDefinitionId }) => {
    const [serverErrors, setServerErrors] = useState<DataResult<WorkflowTaskDefinitionCreateModel>>({ isSuccesful: true, messages: [], data: undefined });
    const { handleResult, handleException } = useNotifications();

    const [formData, setFormData] = useState<WorkflowTaskDefinitionCreateModel>({
        workflowStepDefinitionId: workflowStepDefinitionId,
        taskTypeId: 0,
        name: '',
        taskOrder: 0,
        isRequired: false,
        forwardsWorkflow: true,
        duration: 1,
        reminderInterval: 0,
        description: '',
        codeTaskName: '',
        parameters: '',
        codeTaskParameterInfos: [],
        userDefinedFieldsIds: [],
        documents:[],
    });

    function submit() {
        if (formData) {
            DataService
                .create<WorkflowTaskDefinitionCreateModel>(`api/v1/WorkflowTaskDefinitions/`, formData)
                .then(response => {
                    handleResult(response);
                    setServerErrors(response);
                    if (response.isSuccesful === true && response.data)
                        onHide()
                })
                .catch(e => handleException(e));
        }
    }

    function onFieldChanged(name: string, value: string) {
        if (name === 'attachmentCount')
        {
            const count = parseInt(value);
            if (count > formData.documents.length)
            {
                for (let i = formData.documents.length; i < count; i++)
                {
                    formData.documents.push({ attachmentType: { name: '' } as AttachmentTypeModel } as WorkflowTaskAddDocumentModel);
                }
            }

            if (count < formData.documents.length) {
                formData.documents.splice(count, formData.documents.length - count);
            }
        }
    }

    useEffect(() => {
        getCodeTaskParameterInfo(formData?.codeTaskName)
    }, [formData?.codeTaskName])

    function getCodeTaskParameterInfo(codeTaskName?: string) {
        if (formData && codeTaskName) {
            DataService
                .get<CodeTaskParameterInfo[]>(`api/v1/CodeTaskParameterInfos/ByCodeTaskName/${codeTaskName}`)
                .then(response => {
                    if (!formData.codeTaskParameterInfos)
                        formData.codeTaskParameterInfos = []

                    for (let i = 0; i < response.length; i++) {
                        for (let j = 0; j < formData.codeTaskParameterInfos.length; j++) {
                            if (response[i].name == formData.codeTaskParameterInfos[j].name)
                                response[i].value = formData.codeTaskParameterInfos[j].value
                        }
                    }
                    formData.codeTaskParameterInfos = response

                    //console.log('formData', formData)
                    setFormData(() => { return { ...formData } })
                })
                .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'taskOrder', label: 'Task priority', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'taskTypeId', label: 'Task type', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'taskCategoryId', label: 'Task category', labelSize: 5 },
            { name: 'initialTaskStatusId', label: 'Initial status', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'assignToFunctionalRoleId', label: 'Assigned to a', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'escalateToFunctionalRoleId', label: 'Escalate to a', labelSize: 5 },
            { name: 'isRequired', label: 'Required', labelSize: 5 },
            { name: 'forwardsWorkflow', label: 'Forwards the workflow after completion', labelSize: 5 },
            { name: 'duration', label: 'Duration', labelSize: 5, validationRules: [{ type: 'isRequired' }] },
            { name: 'reminderInterval', label: 'Reminder interval', labelSize: 5 },
            { name: 'description', label: 'Description', labelSize: 5 },
            { name: 'businessPartnerTypeId', label: 'Business-partner type', labelSize: 5 },
            { name: 'attachmentTypeId', label: 'Attachment type', labelSize: 5 },
            { name: 'attachmentRequired', label: 'Attachment required', labelSize: 5 },
            { name: 'title', label: 'Title', labelSize: 5 },
            { name: 'userDefinedFieldsIds', label: 'Custom fields', labelSize: 5 },
            { name: 'codeTaskName', label: 'Code-task', labelSize: 5 },
            { name: 'parameters', label: 'Parameters', labelSize: 5 },
            { name: 'attachmentCount', label: 'Nr of Attachments', labelSize: 5 },

        ],
        handleSubmit: submit,
        handleFieldChanged: onFieldChanged,
        showErrors: 'always',
    };

    if (formData?.codeTaskParameterInfos) {
        formData.codeTaskParameterInfos.forEach((parInfo, index) => {
            formConfig.fields.push({ name: `codeTaskParameterInfos.${index}.value`, label: parInfo.name })
        })
    }

    if (formData?.documents) {
        formData.documents.forEach((doc, index) => {
            formConfig.fields.push({ name: `documents.${index}.attachmentTypeId`, label: 'Type', validationRules: [{ type: 'isRequired' }] })
            formConfig.fields.push({ name: `documents.${index}.attachmentRequired`, label: 'Required' })
        })
    }

    const { getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    return (
        <Popup show={show} onHide={onHide} size={'xl'} title={'Add task'} >
            <Popup.Body>
                <WorkflowTaskControl getFieldProps={getFieldProps} formData={formData} workflowDefinitionId={workflowDefinitionId} />
            </Popup.Body>
            <Popup.Footer>
                <SaveButton {...getSubmitButtonProps()} text='Add' />
            </Popup.Footer>
        </Popup>
    );
}
export default AddWorkflowTask;